import { useEffect, useRef, useState } from "react";
import Button from "./Button";

const ZENDESK_CLOSE_EVENT = "zendesk-chat-close-click";

const ZendeskChatEndConfirmation = () => {
  const dialogRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState(null);

  const close = () => {
    dialogRef.current?.close();
    setIsOpen(false);
  };

  useEffect(() => {
    const handleOpen = () => {
      if (dialogRef.current && !dialogRef.current.open) {
        dialogRef.current.showModal();
        setIsOpen(true);
      }
    };

    window.addEventListener(ZENDESK_CLOSE_EVENT, handleOpen);
    return () => window.removeEventListener(ZENDESK_CLOSE_EVENT, handleOpen);
  }, []);

  const updatePositionRelativeToChat = () => {
    const iframe =
      document.querySelector('iframe[title="Zendesk messaging window"]') ||
      document.querySelector('iframe[title="Zendesk messaging"]') ||
      document.querySelector('iframe[title="Messaging window"]');

    if (!iframe) {
      setPosition(null);
      return;
    }

    const rect = iframe.getBoundingClientRect();
    const width = Math.min(rect.width - 48, 420);
    const left = rect.left + rect.width / 2;
    const top = rect.top + rect.height / 2;

    setPosition({
      top,
      left,
      width: Math.max(280, width),
    });
  };

  useEffect(() => {
    if (!isOpen) {
      setPosition(null);
      return;
    }

    updatePositionRelativeToChat();
    const handleResizeOrScroll = () => updatePositionRelativeToChat();

    window.addEventListener("resize", handleResizeOrScroll);
    window.addEventListener("scroll", handleResizeOrScroll, true);

    return () => {
      window.removeEventListener("resize", handleResizeOrScroll);
      window.removeEventListener("scroll", handleResizeOrScroll, true);
    };
  }, [isOpen]);

  const handleConfirm = () => {
    try {
      if (typeof window.zE === "function") {
        window.zE("messenger", "close");
      }
    } finally {
      close();
    }
  };

  // Close when clicking the backdrop area (target is the <dialog> element itself)
  const handleDialogClick = (e) => {
    if (e.target === dialogRef.current) {
      close();
    }
  };

  return (
    <dialog
      ref={dialogRef}
      className="rounded-[16px] shadow-2xl border-0 p-0 bg-white"
      style={{
        top: position ? position.top : "50%",
        left: position ? position.left : "50%",
        width: position ? position.width : 420,
        maxWidth: "calc(100vw - 40px)",
        transform: "translate(-50%, -50%)",
        margin: 0,
      }}
      onClick={handleDialogClick}
      onCancel={(e) => { e.preventDefault(); close(); }}
    >
      <div className="text-center px-8 py-6">
        <p className="text-[16px] text-gray-800 font-semibold leading-snug mb-6">
          Are you sure you wish to end your chat session?
        </p>
        <div className="flex flex-col gap-3">
          <Button type="primary" text="Yes, End Chat" className="w-full" onClick={handleConfirm} />
          <Button type="primary-outline" text="Cancel" className="w-full" onClick={close} />
        </div>
      </div>
    </dialog>
  );
};

export default ZendeskChatEndConfirmation;
